import React, { useEffect, useState, useRef } from 'react';
import { cn } from '../../lib/utils';
interface SliderProps {
  min: number;
  max: number;
  value: number[];
  onChange: (value: number[]) => void;
  step?: number;
  className?: string;
  formatLabel?: (value: number) => string;
}
export function Slider({
  min,
  max,
  value,
  onChange,
  step = 1,
  className,
  formatLabel = v => v.toString()
}: SliderProps) {
  const [isDragging, setIsDragging] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const getPercentage = (val: number) => (val - min) / (max - min) * 100;
  const handleMouseDown = (index: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(index);
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging === null || !trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const percentage = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
      const rawValue = percentage * (max - min) + min;
      const newValue = Math.round(rawValue / step) * step;
      const nextValue = [...value];
      nextValue[isDragging] = newValue;
      // Ensure values don't cross
      if (value.length === 2) {
        if (isDragging === 0 && newValue > value[1]) nextValue[isDragging] = value[1];
        if (isDragging === 1 && newValue < value[0]) nextValue[isDragging] = value[0];
      }
      onChange(nextValue);
    };
    const handleMouseUp = () => {
      setIsDragging(null);
    };
    if (isDragging !== null) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, max, min, step, value, onChange]);
  return <div className={cn('relative w-full h-6 flex items-center select-none touch-none', className)} data-id="element-428">
      <div ref={trackRef} className="relative w-full h-2 bg-[#0F0F1E] rounded-full overflow-hidden border border-gray-800" data-id="element-429">
        <div className="absolute h-full bg-primary transition-all duration-75" style={{
        left: `${value.length === 2 ? getPercentage(value[0]) : 0}%`,
        right: `${100 - getPercentage(value[value.length - 1])}%`
      }} data-id="element-430" />
      </div>

      {value.map((val, index) => <div key={index} className={cn('absolute w-5 h-5 rounded-full border-2 border-primary bg-[#1A1A2E] cursor-pointer hover:scale-110 transition-transform shadow-lg shadow-primary/20', isDragging === index && 'scale-110 ring-2 ring-accent/50')} style={{
      left: `calc(${getPercentage(val)}% - 10px)`
    }} onMouseDown={handleMouseDown(index)} data-id="element-431">
          <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-primary/10 text-primary text-xs px-2 py-0.5 rounded border border-primary/20 whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity" data-id="element-432">
            {formatLabel(val)}
          </div>
        </div>)}
    </div>;
}